/** Shared FNV seed and motion timelines from Web GuideOrb / GuideOrbButton.module.css. */
export const GUIDE_ORB_BLUE = '#4ABDE9';
export const SUBAGENT_AVATAR_VARIANTS = ['square', 'cloud', 'diamond', 'headphones', 'round-glasses', 'triangle-glasses'] as const;
export type SubagentAvatarVariant = typeof SUBAGENT_AVATAR_VARIANTS[number];
export type GuideOrbVariant = 'orb' | SubagentAvatarVariant;
export function getSubagentAvatarVariant(seed: string): SubagentAvatarVariant {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index++) hash = Math.imul(hash ^ seed.charCodeAt(index), 16777619);
  return SUBAGENT_AVATAR_VARIANTS[(hash >>> 0) % SUBAGENT_AVATAR_VARIANTS.length];
}
export const GAZE_TIMES = [0, .12, .24, .36, .48, .60, .68, .74, .80, .86, 1];
export const GAZE_X = [0, 0, 1.1, 1.1, -1.1, -1.1, 0, 0, 0, 0, 0];
export const LEFT_GAZE_Y = [0, 0, 1.4, 1.4, 0, 0, 1.4, -.9, 1, 0, 0];
export const RIGHT_GAZE_Y = [0, 0, 0, 0, 1.4, 1.4, 1.4, -.9, 1, 0, 0];
export const RIG_GAZE_Y = [0, 0, .7, .7, .7, .7, 1.4, -.9, 1, 0, 0];
export const GAZE_ROTATION = ['0deg', '0deg', '-20deg', '-20deg', '20deg', '20deg', '0deg', '0deg', '0deg', '0deg', '0deg'];
export const LEFT_SCALE_X = [1, 1, .78, .78, 1.26, 1.26, 1, 1, 1, 1, 1];
export const LEFT_SCALE_Y = [1, 1, .7, .7, 1.36, 1.36, 1, 1, 1, 1, 1];
export const RIGHT_SCALE_X = [1, 1, 1.26, 1.26, .78, .78, 1, 1, 1, 1, 1];
export const RIGHT_SCALE_Y = [1, 1, 1.36, 1.36, .7, .7, 1, 1, 1, 1, 1];
export const BLINK_TIMES = [0, .42307692, .43365385, .43653846, .45, .47115385, .89423077, .90480769, .90769231, .92115385, .94230769, .95673077, .96634615, .96923077, .98076923, 1];
export const BLINK_X = [1, 1, 1.035, 1.035, .985, 1, 1, 1.035, 1.035, .985, 1, 1, 1.02, 1.02, .993, 1];
export const BLINK_Y = [1, 1, .025, .025, 1.075, 1, 1, .025, .025, 1.075, 1, 1, .055, .055, 1.04, 1];
export const GAZE_DURATION_MS = 2800;
export const BLINK_DURATION_MS = 5200;
export function shouldAnimateGuideOrb(animated: boolean, reducedMotion: boolean, appActive: boolean) {
  return animated && !reducedMotion && appActive;
}

/** Cubic CSS easing sampled once by Animated's native driver when a segment starts. */
export function guideOrbBezier(x1: number, y1: number, x2: number, y2: number) {
  const curve = (t: number, a: number, b: number) => 3 * (1 - t) * (1 - t) * t * a + 3 * (1 - t) * t * t * b + t * t * t;
  return (x: number) => {
    if (x <= 0 || x >= 1) return x <= 0 ? 0 : 1;
    let low = 0, high = 1;
    for (let index = 0; index < 18; index++) {
      const midpoint = (low + high) / 2;
      if (curve(midpoint, x1, x2) < x) low = midpoint; else high = midpoint;
    }
    return curve((low + high) / 2, y1, y2);
  };
}
export const GUIDE_ORB_EASING = {
  gaze: guideOrbBezier(.42, 0, .58, 1),
  close: guideOrbBezier(.4, 0, .8, .4),
  open: guideOrbBezier(.16, .65, .28, 1),
  settle: guideOrbBezier(.22, .65, .35, 1),
  linear: (time: number) => time,
};

const percent = (time: number) => `${+(time * 100).toFixed(6)}%`;
const gazeFrames = (ys: readonly number[], xs: readonly number[], scalesX: readonly number[], scalesY: readonly number[]) => GAZE_TIMES.map((time, index) =>
  `${percent(time)}{transform:translate(${xs[index]}px,${ys[index]}px) rotate(${GAZE_ROTATION[index]}) scale(${scalesX[index]},${scalesY[index]});}`).join('');
const rigFrames = GAZE_TIMES.map((time, index) => `${percent(time)}{translate:calc(${GAZE_X[index]}px * var(--gaze-direction,1)) calc(${RIG_GAZE_Y[index]}px * var(--gaze-direction,1));}`).join('');
const blinkFrames = BLINK_TIMES.map((time, index) => {
  const easing = [0, 1, 5, 6, 10, 11, 15].includes(index) ? 'cubic-bezier(.4,0,.8,.4)'
    : [2, 7, 12].includes(index) ? 'linear'
    : [3, 8, 13].includes(index) ? 'cubic-bezier(.16,.65,.28,1)' : 'cubic-bezier(.22,.65,.35,1)';
  return `${percent(time)}{transform:scale(${BLINK_X[index]},${BLINK_Y[index]});animation-timing-function:${easing};}`;
}).join('');
/** Web CSS and the native driver derive their frames from the same arrays. */
export const GUIDE_ORB_MOTION_CSS = `
@keyframes dobiGuideEyeL{${gazeFrames(LEFT_GAZE_Y, GAZE_X, LEFT_SCALE_X, LEFT_SCALE_Y)}}
@keyframes dobiGuideEyeR{${gazeFrames(RIGHT_GAZE_Y, GAZE_X, RIGHT_SCALE_X, RIGHT_SCALE_Y)}}
@keyframes dobiGuideRig{${rigFrames}}
@keyframes dobiGuideBlink{${blinkFrames}}
[data-guide-orb][data-guide-orb-motion="true"] [data-guide-orb-eye]{animation-duration:${GAZE_DURATION_MS}ms;animation-timing-function:ease-in-out;animation-iteration-count:infinite;}
[data-guide-orb][data-guide-orb-motion="true"] [data-guide-orb-eye="left"]{animation-name:dobiGuideEyeL;}
[data-guide-orb][data-guide-orb-motion="true"] [data-guide-orb-eye="right"]{animation-name:dobiGuideEyeR;}
[data-guide-orb][data-guide-orb-motion="true"] [data-guide-orb-eye]::after{animation:dobiGuideBlink ${BLINK_DURATION_MS}ms linear infinite;}
[data-guide-orb][data-guide-orb-motion="true"] [data-guide-orb-rig]{animation:dobiGuideRig ${GAZE_DURATION_MS}ms ease-in-out infinite;}
[data-guide-orb][data-guide-orb-motion="true"] [data-guide-orb-rig] [data-guide-orb-eye="left"]{animation-name:dobiGuideEyeL,dobiGuideRig;}
[data-guide-orb][data-guide-orb-motion="true"] [data-guide-orb-rig] [data-guide-orb-eye="right"]{animation-name:dobiGuideEyeR,dobiGuideRig;}
@media(prefers-reduced-motion:reduce){[data-guide-orb-motion] [data-guide-orb-eye],[data-guide-orb-motion] [data-guide-orb-eye]::after,[data-guide-orb-motion] [data-guide-orb-rig]{animation:none!important;}}
`;
